import { useCallback } from "react";
import {
  type MeCompanyConfigurationFragment,
  type UpdateCompanyConfigurationInput,
  useUpdateCompanyConfigurationMutation,
  useUpdateQuoteAgentStaffSettingsMutation,
} from "../../generated/graphql";
import {
  crossCompanyUpdateRefusal,
  hasSessionScopedChanges,
  splitStaffOnlyUpdate,
} from "./company-configurations-utils";

export type SaveOutcome =
  | { readonly status: "nothing" }
  | { readonly status: "refused"; readonly message: string }
  | { readonly status: "failed"; readonly message: string }
  | {
      readonly status: "saved";
      readonly saved: MeCompanyConfigurationFragment | undefined;
    };

type SaveRequest = {
  readonly companyUuid: string;
  readonly sessionCompanyUuid: string | undefined;
  readonly update: UpdateCompanyConfigurationInput;
};

/**
 * Sends the staff-only keys first through `updateQuoteAgentStaffSettings`, then
 * the rest through `updateCompanyConfiguration` only if that succeeded. A failure
 * of the second leaves the first committed, and the outcome message says so.
 */
export const useSaveCompanyConfiguration = () => {
  const [updateCompanyConfiguration, { loading: updateLoading }] =
    useUpdateCompanyConfigurationMutation();
  const [updateQuoteAgentStaffSettings, { loading: staffUpdateLoading }] =
    useUpdateQuoteAgentStaffSettingsMutation();

  const save = useCallback(
    async ({
      companyUuid,
      sessionCompanyUuid,
      update,
    }: SaveRequest): Promise<SaveOutcome> => {
      const refusal = crossCompanyUpdateRefusal(
        companyUuid,
        sessionCompanyUuid,
        update,
      );
      if (refusal !== undefined) {
        return { status: "refused", message: refusal };
      }
      const { staffOnly, rest } = splitStaffOnlyUpdate(update);
      const hasStaffOnlyChanges = Object.keys(staffOnly).length > 0;
      const hasSessionChanges = hasSessionScopedChanges(rest);
      if (!hasStaffOnlyChanges && !hasSessionChanges) {
        return { status: "nothing" };
      }
      const messages: string[] = [];
      let saved: MeCompanyConfigurationFragment | undefined;
      if (hasStaffOnlyChanges) {
        try {
          const response = await updateQuoteAgentStaffSettings({
            variables: { input: { companyUuid, ...staffOnly } },
          });
          messages.push(...(response.errors ?? []).map((err) => err.message));
          saved = response.data?.updateQuoteAgentStaffSettings;
        } catch (error) {
          messages.push(`Error: ${String(error)}`);
        }
        if (messages.length > 0) {
          messages.unshift(
            hasSessionChanges
              ? "Staff-only settings were not saved and the other settings were not sent"
              : "Staff-only settings were not saved",
          );
        }
      }
      if (hasSessionChanges && messages.length === 0) {
        try {
          const response = await updateCompanyConfiguration({
            variables: { updateCompanyConfigurationInput: rest },
          });
          messages.push(...(response.errors ?? []).map((err) => err.message));
          saved = response.data?.updateCompanyConfiguration ?? saved;
        } catch (error) {
          messages.push(`Error: ${String(error)}`);
        }
        if (messages.length > 0) {
          messages.unshift(
            hasStaffOnlyChanges
              ? "Staff-only settings were saved but the other settings were not"
              : "Settings were not saved",
          );
        }
      }
      return messages.length === 0
        ? { status: "saved", saved }
        : { status: "failed", message: messages.join(", ") };
    },
    [updateCompanyConfiguration, updateQuoteAgentStaffSettings],
  );

  return { save, saving: updateLoading || staffUpdateLoading };
};
